import { RefObject, useCallback, useState } from 'react';

import { PlayerControlsProps } from '../components/Controls/types';
import { PlayerVideoRef } from '../components/PlayerVideo/PlayerVideo.types';

export const IS_WATCHING_LIVE_THRESHOLD = 5;

interface UseLiveControlsProps {
  playerVideoRef?: RefObject<PlayerVideoRef | null>;
  isLive?: boolean;
  totalDurationSeconds?: number;
  currentTimeInSeconds?: number;
  onSeekTo?: PlayerControlsProps['onSeekTo'];
}

export interface UseLiveControlsReturn {
  isWatchingLive: boolean;
  setIsWatchingLive: (isWatching: boolean) => void;
  goLive: () => void;
  calculateIsWatchingLive: (seekTime?: number) => void;
  setHasManuallySeekd: (hasSeekd: boolean) => void;
}

/**
 * Hook to manage live stream controls
 * This hook extracts the live functionality from PlayerTimeline
 * so it can be used independently in other components
 */
export default function useLiveControls({
  playerVideoRef,
  isLive = false,
  totalDurationSeconds = 0,
  currentTimeInSeconds = 0,
  onSeekTo,
}: UseLiveControlsProps): UseLiveControlsReturn {
  const [isWatchingLive, setIsWatchingLive] = useState(true);
  const [hasManuallySeekd, setHasManuallySeekd] = useState(false);

  /**
   * Jump to live position
   */
  const goLive = useCallback(() => {
    if (!isLive || !playerVideoRef?.current) {
      return;
    }

    const latestSeekableTime = playerVideoRef.current.getLatestSeekableTime();

    onSeekTo?.(latestSeekableTime);

    setIsWatchingLive(true);

    // Reset the manual seek flag when going live
    setHasManuallySeekd(false);
  }, [isLive, playerVideoRef, onSeekTo]);

  /**
   * Calculate if we're watching live based on current time and total duration
   */
  const calculateIsWatchingLive = useCallback(
    (seekTime?: number) => {
      if (!isLive || !totalDurationSeconds) {
        return;
      }

      //in case currentTimeInSeconds hasn't yet been updated like when triggered from
      //handleMoveEnd in useDragEvents, pass the seekTime to this function
      const timeToCheck =
        seekTime !== undefined ? seekTime : currentTimeInSeconds;

      const isNearEnd =
        totalDurationSeconds - timeToCheck <= IS_WATCHING_LIVE_THRESHOLD;

      setIsWatchingLive(isNearEnd);

      // Only automatically go live if we're near the end and haven't manually seeked
      if (isNearEnd && !hasManuallySeekd) {
        goLive();
      }
    },
    [
      currentTimeInSeconds,
      totalDurationSeconds,
      goLive,
      hasManuallySeekd,
      isLive,
    ]
  );

  return {
    isWatchingLive,
    setIsWatchingLive,
    goLive,
    calculateIsWatchingLive,
    setHasManuallySeekd,
  };
}
